/**
 * ContrattoTemplateList Component
 * Feature: Legal - Lista template contratti
 */

import { useState } from "react";
import { Plus, FileText, Trash2, Pencil } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useContrattoTemplates, useDeleteContrattoTemplate } from "../hooks";
import type { ContrattoTemplate } from "../types";

export function ContrattoTemplateList() {
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  
  const { data: templates = [], isLoading } = useContrattoTemplates();
  const deleteTemplate = useDeleteContrattoTemplate();

  const handleDelete = (id: string) => {
    deleteTemplate.mutate(id, { onSuccess: () => setConfirmDelete(null) });
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-40 mb-2" />
            <Skeleton className="h-4 w-56" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-40 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Template contratti</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Crea i modelli da usare nelle prenotazioni</p>
        </div>
        <button 
          onClick={() => window.location.href = "/contratti/nuovo"}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" /> Nuovo template
        </button>
      </div>

      {/* Contatori */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-2xl font-bold text-emerald-400">{templates.filter(t => t.attivo).length}</div>
          <div className="text-xs text-muted-foreground">Template attivi</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-2xl font-bold text-muted-foreground">{templates.filter(t => !t.attivo).length}</div>
          <div className="text-xs text-muted-foreground">Non attivi</div>
        </div>
      </div>

      {/* Lista template */}
      {templates.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-14 text-center">
          <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-sm font-medium text-foreground mb-1">Nessun template</p>
          <p className="text-xs text-muted-foreground mb-4">Crea il primo modello di contratto da usare nelle prenotazioni.</p>
          <button 
            onClick={() => window.location.href = "/contratti/nuovo"}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" /> Nuovo template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t: ContrattoTemplate) => (
            <div key={t.id} className="bg-card border border-border rounded-2xl p-4 hover:shadow-sm transition-shadow">
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-muted/60 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-4 w-4 text-muted-foreground/50" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-foreground text-sm leading-tight truncate">{t.nome}</h3>
                    <p className="text-[11px] text-muted-foreground">{t.tipo}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${t.attivo ? "bg-emerald-500/10 text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                  {t.attivo ? "Attivo" : "Non attivo"}
                </span>
              </div>

              {t.updated_at && (
                <div className="text-[11px] text-muted-foreground mb-3">
                  Ultima modifica: {new Date(t.updated_at).toLocaleDateString("it-IT")}
                </div>
              )}

              {/* Azioni */}
              <div className="flex gap-2 pt-2.5 border-t border-border">
                <button 
                  onClick={() => window.location.href = `/contratti/${t.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 border border-border rounded-xl py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                >
                  <Pencil className="h-3 w-3" /> Modifica
                </button>
                {confirmDelete === t.id ? (
                  <div className="flex gap-1.5">
                    <button 
                      onClick={() => handleDelete(t.id)}
                      disabled={deleteTemplate.isPending}
                      className="px-2.5 py-1.5 rounded-xl bg-rose-500 text-white text-xs font-medium disabled:opacity-60"
                    >
                      {deleteTemplate.isPending ? "..." : "Elimina"}
                    </button>
                    <button 
                      onClick={() => setConfirmDelete(null)}
                      className="px-2.5 py-1.5 rounded-xl border border-border text-xs text-muted-foreground"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => setConfirmDelete(t.id)}
                    className="flex items-center justify-center border border-border rounded-xl px-2.5 py-1.5 text-rose-400 hover:border-rose-400 hover:bg-rose-500/10 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
